import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Award, UserPlus, X, Check, Mail, Search, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

/**
 * Trainer-system widget that lives on the Profile page.
 *
 * Surfaces everything client-side that a profile owner cares about:
 *   - Trainer-mode toggle (self-service)
 *   - Outgoing trainer requests
 *   - My connected trainers list + disconnect
 *   - Pending workout suggestions from my trainer(s) + accept/decline
 *
 * WS live propagation:
 *   - trainer:accepted / trainer:declined → refresh outgoing + trainers list
 *   - trainer:workout_suggested → refresh suggestions inline (no refresh!)
 */
export default function TrainerProfileWidget() {
  const { user, refresh: refreshMe } = useAuth();
  const { subscribe } = useWebSocket();

  // ----- Trainer mode (self-service) -----
  const [tform, setTform] = useState({
    is_trainer: user?.is_trainer || false,
    trainer_bio: user?.trainer_bio || "",
    trainer_specialties: (user?.trainer_specialties || []).join(", "),
    trainer_location: user?.trainer_location || "",
    trainer_public: user?.trainer_public ?? false,
    trainer_accepting_clients: user?.trainer_accepting_clients ?? true,
  });
  const [savingTrainer, setSavingTrainer] = useState(false);

  // ----- Trainer requests/relationships (as client) -----
  const [outgoing, setOutgoing] = useState([]);
  const [myTrainers, setMyTrainers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [o, t, s] = await Promise.all([
        api.get("/trainer-requests/outgoing"),
        api.get("/me/trainers"),
        api.get("/me/workout-suggestions"),
      ]);
      setOutgoing(o.data || []);
      setMyTrainers(t.data || []);
      setSuggestions(s.data || []);
    } catch {
      // Quietly tolerate — these are non-critical lists; UI just stays empty.
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!user) return;
    setTform({
      is_trainer: user.is_trainer || false,
      trainer_bio: user.trainer_bio || "",
      trainer_specialties: (user.trainer_specialties || []).join(", "),
      trainer_location: user.trainer_location || "",
      trainer_public: user.trainer_public ?? false,
      trainer_accepting_clients: user.trainer_accepting_clients ?? true,
    });
  }, [user]);

  // Live WS hooks — refresh state without forcing a manual page reload.
  useEffect(() => {
    const unsubs = [
      subscribe("trainer:accepted", () => { toast.success("A trainer accepted your request 🎉"); loadAll(); }),
      subscribe("trainer:declined", () => { toast("A trainer declined your request"); loadAll(); }),
      subscribe("trainer:workout_suggested", (m) => {
        toast.success(`${m.trainer_name || "Your trainer"} suggested: ${m.workout_name || "a workout"}`);
        loadAll();
      }),
    ];
    return () => unsubs.forEach((u) => u && u());
  }, [subscribe, loadAll]);

  const saveTrainerProfile = async () => {
    setSavingTrainer(true);
    try {
      const payload = {
        is_trainer: tform.is_trainer,
        trainer_bio: tform.trainer_bio?.trim() || null,
        trainer_specialties: tform.trainer_specialties
          .split(/[,\n]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        trainer_location: tform.trainer_location?.trim() || null,
        trainer_public: !!tform.trainer_public,
        trainer_accepting_clients: !!tform.trainer_accepting_clients,
      };
      await api.put("/me/trainer-profile", payload);
      toast.success(payload.is_trainer ? "Trainer profile saved" : "Trainer mode disabled");
      await refreshMe();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save trainer profile");
    } finally {
      setSavingTrainer(false);
    }
  };

  const sendEmailInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSendingInvite(true);
    try {
      const { data } = await api.post("/trainers/request", {
        to_email: inviteEmail.trim(),
        message: inviteMessage.trim() || null,
      });
      if (data.email_configured === false) {
        toast.success("Invite created. Email service not configured — share this link manually:");
        // Best-effort: copy invite URL to clipboard.
        if (data.invite_url && navigator.clipboard) {
          await navigator.clipboard.writeText(data.invite_url).catch(() => {});
          toast.success("Invite link copied to clipboard");
        }
      } else {
        toast.success(`Invite emailed to ${inviteEmail.trim()}`);
      }
      setInviteOpen(false);
      setInviteEmail("");
      setInviteMessage("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  const disconnectTrainer = async (trainer_user_id, name) => {
    if (!window.confirm(`Disconnect ${name || "trainer"}?`)) return;
    try {
      await api.delete(`/me/trainers/${trainer_user_id}`);
      toast.success("Trainer disconnected");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't disconnect");
    }
  };

  const acceptSuggestion = async (sid) => {
    try {
      const { data } = await api.post(`/me/workout-suggestions/${sid}/accept`);
      toast.success("Workout added to your library");
      loadAll();
      if (data.workout_id) setTimeout(() => { window.location.href = `/workouts/${data.workout_id}`; }, 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't accept");
    }
  };

  const declineSuggestion = async (sid) => {
    try {
      await api.post(`/me/workout-suggestions/${sid}/decline`);
      toast("Suggestion declined");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't decline");
    }
  };

  return (
    <div data-testid="trainer-widget" className="space-y-5">
      {/* Pending workout suggestions (TOP — highest action priority) */}
      {suggestions.length > 0 && (
        <div data-testid="trainer-suggestions-block" className="border border-blue-500/40 bg-blue-500/5 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4 text-blue-500" />
            <h2 className="font-display text-xl uppercase">Workout suggestions from your trainer</h2>
          </div>
          <div className="space-y-2">
            {suggestions.map((s) => (
              <div key={s.suggestion_id} data-testid={`suggestion-${s.suggestion_id}`} className="border border-neutral-800 p-3 bg-neutral-950">
                <div className="text-xs text-neutral-400 uppercase tracking-widest">From {s.trainer_name || "Your trainer"}</div>
                <div className="font-display text-base mt-0.5">{s.workout_name || "Untitled workout"}</div>
                {s.message && <div className="text-sm text-neutral-300 mt-1.5 italic">"{s.message}"</div>}
                <div className="flex gap-2 mt-3">
                  <Button data-testid={`accept-suggestion-${s.suggestion_id}`} size="sm" onClick={() => acceptSuggestion(s.suggestion_id)} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
                    <Check className="w-4 h-4 mr-1.5" /> Accept & Add
                  </Button>
                  <Button data-testid={`decline-suggestion-${s.suggestion_id}`} size="sm" variant="ghost" onClick={() => declineSuggestion(s.suggestion_id)} className="text-neutral-400 hover:text-white rounded-none uppercase tracking-wider font-display h-9">
                    <X className="w-4 h-4 mr-1.5" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My trainers list + find a trainer CTA */}
      <div className="border border-neutral-800 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl uppercase">My Trainers</h2>
          <div className="flex gap-2">
            <Link to="/trainers" data-testid="find-trainer-link">
              <Button data-testid="find-trainers-btn" size="sm" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
                <Search className="w-3 h-3 mr-1.5" /> Find
              </Button>
            </Link>
            <Button data-testid="invite-trainer-email-btn" size="sm" variant="outline" onClick={() => setInviteOpen(true)} className="border-neutral-700 hover:border-blue-500 rounded-none uppercase tracking-wider font-display h-9">
              <Mail className="w-3 h-3 mr-1.5" /> Invite via Email
            </Button>
          </div>
        </div>
        {myTrainers.length === 0 ? (
          <p className="text-xs text-neutral-500">No trainers connected yet. Find one in the directory or invite by email.</p>
        ) : (
          <div className="space-y-2">
            {myTrainers.map((t) => (
              <div key={t.relationship_id} data-testid={`trainer-card-${t.trainer_user_id}`} className="flex items-start justify-between gap-3 border border-neutral-800 p-3 bg-neutral-950">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base">{t.trainer_name}</div>
                  {t.trainer_bio && <div className="text-xs text-neutral-400 mt-1 line-clamp-2">{t.trainer_bio}</div>}
                  {t.trainer_specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {t.trainer_specialties.map((s, i) => (
                        <span key={i} className="text-[10px] uppercase tracking-widest text-blue-500 border border-blue-500/30 px-1.5 py-0.5">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
                <Button data-testid={`disconnect-trainer-${t.trainer_user_id}`} size="icon" variant="ghost" onClick={() => disconnectTrainer(t.trainer_user_id, t.trainer_name)} className="text-neutral-500 hover:text-blue-500 h-8 w-8 shrink-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Outgoing trainer requests — visible until accepted/declined */}
      {outgoing.filter((r) => r.status === "pending").length > 0 && (
        <div data-testid="outgoing-requests-block" className="border border-neutral-800 p-5">
          <h3 className="font-display text-base uppercase mb-2">Pending requests sent</h3>
          <div className="space-y-1.5">
            {outgoing.filter((r) => r.status === "pending").map((r) => (
              <div key={r.request_id} data-testid={`outgoing-request-${r.request_id}`} className="flex items-center justify-between text-xs text-neutral-300 border-b border-neutral-900 pb-1.5">
                <span>{r.to_email ? `Email invite → ${r.to_email}` : "Direct request"}</span>
                <span className="text-neutral-500">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trainer mode (self-service) */}
      <div className="border border-neutral-800 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl uppercase">I'm a Trainer</h2>
          <Switch
            data-testid="trainer-mode-toggle"
            checked={!!tform.is_trainer}
            onCheckedChange={(v) => setTform({ ...tform, is_trainer: v })}
          />
        </div>
        <p className="text-xs text-neutral-500 mb-3">Toggle on to receive trainer requests, view client progress, and assign workouts.</p>

        {tform.is_trainer && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Bio</Label>
              <Textarea
                data-testid="trainer-bio-input"
                value={tform.trainer_bio}
                onChange={(e) => setTform({ ...tform, trainer_bio: e.target.value })}
                maxLength={600}
                rows={3}
                placeholder="15 yrs strength & conditioning. Powerlifting + rehab. Available evenings."
                className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Specialties (comma-separated)</Label>
              <Input
                data-testid="trainer-specialties-input"
                value={tform.trainer_specialties}
                onChange={(e) => setTform({ ...tform, trainer_specialties: e.target.value })}
                placeholder="Strength, Mobility, Cardio"
                className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-10"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Location</Label>
              <Input
                data-testid="trainer-location-input"
                value={tform.trainer_location}
                onChange={(e) => setTform({ ...tform, trainer_location: e.target.value })}
                maxLength={120}
                placeholder="Adelaide, AU"
                className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-10"
              />
            </div>
            <div className="flex items-center justify-between border-t border-neutral-900 pt-3 mt-3">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold">Public in directory</div>
                <div className="text-[10px] text-neutral-500">Clients can find you in the trainer search.</div>
              </div>
              <Switch
                data-testid="trainer-public-toggle"
                checked={!!tform.trainer_public}
                onCheckedChange={(v) => setTform({ ...tform, trainer_public: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold">Accepting new clients</div>
                <div className="text-[10px] text-neutral-500">Turn off to pause new requests.</div>
              </div>
              <Switch
                data-testid="trainer-accepting-toggle"
                checked={!!tform.trainer_accepting_clients}
                onCheckedChange={(v) => setTform({ ...tform, trainer_accepting_clients: v })}
              />
            </div>
            <Link to="/trainer" data-testid="open-trainer-hub-link" className="block">
              <Button variant="outline" className="w-full border-neutral-700 hover:border-blue-500 rounded-none uppercase tracking-wider font-display h-10 mt-2">
                <Award className="w-4 h-4 mr-2" /> Open Trainer Hub →
              </Button>
            </Link>
          </div>
        )}
        <Button
          data-testid="save-trainer-profile-btn"
          onClick={saveTrainerProfile}
          disabled={savingTrainer}
          className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10 mt-3"
        >
          {savingTrainer ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save Trainer Settings
        </Button>
      </div>

      {/* Email invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase">Invite a trainer by email</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500 tracking-widest uppercase">
              If your trainer isn't on the app yet, we'll email them a one-time invite link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Trainer's email</Label>
              <Input
                data-testid="invite-email-input"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                type="email"
                placeholder="trainer@example.com"
                className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-neutral-400">Message (optional)</Label>
              <Textarea
                data-testid="invite-message-input"
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Hey! I'd love for you to coach me through the app — workouts, tracking, the lot."
                className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none"
              />
            </div>
            <Button
              data-testid="send-invite-btn"
              onClick={sendEmailInvite}
              disabled={!inviteEmail.trim() || sendingInvite}
              className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10"
            >
              {sendingInvite ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Send Invite
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
